import type { SmsLastTestDTO } from '@today/types'
import { FieldShell } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import { type ChangeEvent, useState } from 'react'

/** Why a test SMS was not sent, in the terms the panel shows it (TO-383 Contract errors). */
export enum SmsTestErrorKind {
	INVALID_PHONE = 'invalid-phone',
	RECIPIENT_NOT_ALLOWED = 'recipient-not-allowed',
	COUNTRY_NOT_ALLOWED = 'country-not-allowed',
	SPEND_CAP_REACHED = 'spend-cap-reached',
	CREDENTIALS_REJECTED = 'credentials-rejected',
	PROVIDER_REJECTED = 'provider-rejected',
	PROVIDER_UNAVAILABLE = 'provider-unavailable',
	FAILED = 'failed',
}

const ERROR_MESSAGES: Record<SmsTestErrorKind, string> = {
	[SmsTestErrorKind.INVALID_PHONE]: 'Enter the phone number in international format, e.g. +34600000001.',
	[SmsTestErrorKind.RECIPIENT_NOT_ALLOWED]: 'This number is not allowed to receive test SMS on this stage.',
	[SmsTestErrorKind.COUNTRY_NOT_ALLOWED]: "The destination country is not allowed by the tenant's SMS settings.",
	[SmsTestErrorKind.SPEND_CAP_REACHED]: 'The monthly spend cap is reached: the test SMS was not sent.',
	[SmsTestErrorKind.CREDENTIALS_REJECTED]: 'Twilio rejected the stored credentials.',
	[SmsTestErrorKind.PROVIDER_REJECTED]: 'Twilio refused the message.',
	[SmsTestErrorKind.PROVIDER_UNAVAILABLE]: 'Twilio is unavailable right now. Try again later.',
	[SmsTestErrorKind.FAILED]: 'The test SMS could not be sent. Try again.',
}

/**
 * Props for {@link SmsTestPanel}.
 *
 * @property lastTest - The tenant's newest test SMS, or `null`.
 * @property readOnly - The manifest grants READ: the phone input is disabled and no send is offered.
 * @property isBusy - A test is in flight; sending is disabled.
 * @property sent - The last send was accepted by the provider.
 * @property error - Why the last send was refused, if it was.
 * @property onSend - Sends a test SMS to the given phone (E.164).
 */
export interface SmsTestPanelProps {
	lastTest: SmsLastTestDTO | null
	readOnly?: boolean
	isBusy?: boolean
	sent?: boolean
	error?: SmsTestErrorKind | null
	onSend: (to: string) => void
}

const INPUT_CLASS =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

function LastTest({ lastTest }: { lastTest: SmsLastTestDTO }) {
	const reason = lastTest.failureCode ? ` (${lastTest.failureCode})` : ''
	return (
		<p className="text-ds-sm text-ds-text-muted">{`Last test: ${lastTest.status}${reason} — ${lastTest.createdAt}`}</p>
	)
}

/**
 * Sends a test SMS from the tenant's settings (TO-383 REQ-014) and shows the
 * outcome and the newest test's delivery status. Pure: the page's hook sends.
 */
export function SmsTestPanel({
	lastTest,
	readOnly = false,
	isBusy = false,
	sent = false,
	error,
	onSend,
}: SmsTestPanelProps) {
	const [to, setTo] = useState('')
	return (
		<section className="flex flex-col gap-3" aria-label="Test SMS">
			<h3 className="text-ds-base font-semibold text-ds-text">Test SMS</h3>
			<FieldShell label="Test phone number" description="International format, e.g. +34600000001.">
				<input
					type="tel"
					className={INPUT_CLASS}
					value={to}
					disabled={readOnly}
					onChange={(event: ChangeEvent<HTMLInputElement>) => setTo(event.target.value)}
				/>
			</FieldShell>
			{readOnly ? null : (
				<div>
					<Button variant="primary" isDisabled={isBusy || to.trim() === ''} onPress={() => onSend(to.trim())}>
						Send test
					</Button>
				</div>
			)}
			{sent ? (
				<p role="status" className="text-ds-sm text-ds-text">
					Test message sent
				</p>
			) : null}
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{ERROR_MESSAGES[error]}
				</p>
			) : null}
			{lastTest ? <LastTest lastTest={lastTest} /> : null}
		</section>
	)
}
